import { DefaultLoader, LoaderContainer } from 'components/Loader';
import ProfileNavigation from 'components/profileNavigation';
import Head from 'components/profileNavigation/Head';
import ProfileLink from 'components/profileNavigation/Link';
import React, { FC, useContext, useMemo } from 'react';
import useSWR from 'swr';
import Mentorship from 'types/Mentorship';
import { MenteeEditContext } from '..';
import MenteeMentoringRoutes from './Routes';

type RetrievedData = { mentorship: Mentorship | undefined };

type ContextType = {
  mentorship: Mentorship | undefined;
};
export const MentoringContext = React.createContext({} as ContextType);
const { Provider } = MentoringContext;

const useCustomSWR = (id: number | undefined) => {
  const { data, error } = useSWR(`/mentorship/mentee/${id}/ongoing/`);

  if (!id) {
    return { data: undefined, error: { response: { statut: 400 } } };
  }
  return { data, error };
};

const Mentoring: FC = () => {
  const { mentee } = useContext(MenteeEditContext);

  const { data, error }: { data?: RetrievedData; error?: unknown } =
    useCustomSWR(mentee?.id);

  const value = useMemo(
    () => ({
      mentorship: error ? undefined : data?.mentorship,
    }),
    [data, error],
  );

  return data || error ? (
    <Provider value={value}>
      <section>
        <Head
          title="Mentorat"
          subtitle="Toutes les informations relatives au mentorat et à son historique"
        />
        <Navigation />
        <MenteeMentoringRoutes />
      </section>
    </Provider>
  ) : (
    <LoaderContainer height="20vh">
      <DefaultLoader />
    </LoaderContainer>
  );
};

const Navigation: FC = () => {
  const { mentee } = useContext(MenteeEditContext);
  const baseURL = useMemo(() => {
    return `/admin/mentoring/mentees/${mentee?.id}/mentoring/`;
  }, [mentee]);
  const title: string = useMemo(() => {
    switch (mentee?.menteeStatus) {
      case 'REFUSED':
        return 'Mentorat refusé';
      case 'MENTORSHIP_ACTIVE':
        switch (
          mentee!.MenteeMentorship ? mentee!.MenteeMentorship[0]?.status : ''
        ) {
          case 'WAITING_MENTEE':
          case 'WAITING_MENTOR':
            return 'Mentorat en attente';
          case 'FIRST_INTERVIEW':
          case 'LATE_MENTEE_FIRST_INTERVIEW':
          case 'LATE_MENTOR_FIRST_INTERVIEW':
            return 'Mentorat accepté';

          case 'INTERRUPTED_ADMIN':
            return 'Mentorat interrompu';

          case 'FINISHED_ADMIN':
          case 'FINISHED_DATE':
            return 'Mentorat terminé';

          default:
            return 'Mentorat en cours';
        }
      default:
        return 'Associer un mentor';
    }
  }, [mentee]);
  return (
    <ProfileNavigation>
      <ProfileLink to={baseURL}>{title}</ProfileLink>
      <ProfileLink to={baseURL + 'history'}>Historique</ProfileLink>
      <ProfileLink to={baseURL + 'tracking/'}>Suivi mentoré</ProfileLink>
    </ProfileNavigation>
  );
};

export default Mentoring;
